import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getOffers, createCheckoutSession, getSubscriptionStatus, cancelSubscription, createCustomerPortal } from '../../services/paymentService';
import './PricingPage.css';

function formatValidUntil(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function PricingPage({ onOpenAuth }) {
  const { isAuthenticated } = useAuth();
  const [offers, setOffers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [quota, setQuota] = useState(null);
  const [creditsBreakdown, setCreditsBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [error, setError] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      const offersData = await getOffers();
      setOffers(offersData.offers || []);

      if (isAuthenticated) {
        const subData = await getSubscriptionStatus();
        setSubscription(subData.subscription);
        setQuota(subData.quota);
        setCreditsBreakdown(subData.credits_breakdown ?? null);
      } else {
        setSubscription(null);
        setQuota(null);
        setCreditsBreakdown(null);
      }
    } catch (err) {
      console.error('Erreur chargement offres:', err);
      setError('Impossible de charger les offres. Réessayez plus tard.');
    } finally {
      setLoading(false);
    }
  };

  const handleChooseOffer = async (priceId) => {
    if (!isAuthenticated) {
      onOpenAuth();
      return;
    }

    try {
      setCheckoutLoading(priceId);
      setError(null);
      const data = await createCheckoutSession(priceId);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Erreur checkout:', err);
      setError(err.response?.data?.message || 'Erreur lors de la redirection vers le paiement');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setError(null);
      await cancelSubscription();
      setCancelConfirm(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const handleManageBilling = async () => {
    try {
      const data = await createCustomerPortal();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ouverture du portail');
    }
  };

  const formatPrice = (cents, currency = 'eur') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="pricing-page">
        <div className="pricing-loading">Chargement des offres...</div>
      </div>
    );
  }

  const oneTimeOffers = offers.filter(o => o.type === 'one_time');
  const subscriptionOffers = offers.filter(o => o.type === 'monthly' || o.type === 'yearly');

  const isUnlimited = quota && quota.remaining === null;

  return (
    <div className="pricing-page">
      {error && <div className="pricing-error">{error}</div>}

      {/* Mes crédits — utilisateur connecté */}
      {isAuthenticated && quota && (
        <section className="pricing-my-credits" aria-labelledby="mes-credits-heading">
          <h2 id="mes-credits-heading" className="pricing-section-title">Mes crédits</h2>
          <div className="my-credits-card">
            {isUnlimited ? (
              <p className="my-credits-unlimited">Votre compte bénéficie d’un accès <strong>illimité</strong> aux générations.</p>
            ) : creditsBreakdown ? (
              <ul className="my-credits-list">
                <li className="my-credits-row">
                  <span className="my-credits-label">Recharges</span>
                  <span className="my-credits-value">{creditsBreakdown.recharge} crédit{creditsBreakdown.recharge !== 1 ? 's' : ''}</span>
                </li>
                <li className="my-credits-row">
                  <span className="my-credits-label">Abonnement</span>
                  <span className="my-credits-value">
                    {creditsBreakdown.subscription} crédit{creditsBreakdown.subscription !== 1 ? 's' : ''}
                    {creditsBreakdown.subscription_valid_until && (
                      <span className="my-credits-until">
                        {' '}
                        (valable jusqu’au {formatValidUntil(creditsBreakdown.subscription_valid_until)})
                      </span>
                    )}
                  </span>
                </li>
              </ul>
            ) : (
              <p className="my-credits-fallback">
                Crédits restants : <strong>{quota.remaining ?? 0}</strong>
              </p>
            )}
          </div>
        </section>
      )}

      {/* Nos offres — vitrine commerciale */}
      <section className="pricing-commercial" aria-labelledby="nos-offres-heading">
        <h2 id="nos-offres-heading" className="pricing-section-title">Nos offres</h2>

        {subscriptionOffers.length > 0 && (
          <div className="pricing-section pricing-section--nested">
            <h3 className="pricing-subsection-title">Abonnements</h3>
            <div className="pricing-grid">
              {subscriptionOffers.map((offer) => {
                const isCurrentPlan = subscription && subscription.status === 'active' && subscription.stripe_price_id === offer.id;
                return (
                  <div key={offer.id} className={`pricing-card ${offer.type === 'yearly' ? 'card-featured' : ''} ${isCurrentPlan ? 'card-current' : ''}`}>
                    {offer.type === 'yearly' && (
                      <div className="card-badge">Meilleure offre</div>
                    )}
                    {isCurrentPlan && (
                      <div className="card-badge card-badge-current">Votre plan</div>
                    )}
                    <div className="card-header">
                      <h3>{offer.name}</h3>
                      <div className="card-price">
                        <span className="price-amount">{formatPrice(offer.price_cents, offer.currency)}</span>
                        <span className="price-period">/{offer.type === 'yearly' ? 'an' : 'mois'}</span>
                      </div>
                    </div>
                    <div className="card-body">
                      <p className="card-description">{offer.description}</p>
                      <div className="card-credits">
                        <span className="credits-number">{offer.credits}</span>
                        <span className="credits-label">
                          générations/{offer.type === 'yearly' ? 'an' : 'mois'}
                        </span>
                      </div>
                      {offer.features && offer.features.length > 0 && (
                        <ul className="card-features">
                          {offer.features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {offer.price_cents === 0 ? (
                      // Offre gratuite : plan par défaut attribué automatiquement à l'inscription.
                      // Non souscriptible via Stripe (sinon double attribution de crédits).
                      <button type="button" className="card-button card-button-default" disabled>
                        Offert par défaut
                      </button>
                    ) : isCurrentPlan ? (
                      <button type="button" className="card-button card-button-manage" onClick={handleManageBilling}>
                        Gérer mon abonnement
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="card-button"
                        onClick={() => handleChooseOffer(offer.id)}
                        disabled={checkoutLoading === offer.id}
                      >
                        {checkoutLoading === offer.id ? 'Redirection...' : 'S\'abonner'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {oneTimeOffers.length > 0 && (
          <div className="pricing-section pricing-section--nested">
            <h3 className="pricing-subsection-title">Recharges</h3>
            <div className="pricing-grid">
              {oneTimeOffers.map((offer) => (
                <div key={offer.id} className="pricing-card">
                  <div className="card-header">
                    <h3>{offer.name}</h3>
                    <div className="card-price">
                      <span className="price-amount">{formatPrice(offer.price_cents, offer.currency)}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <p className="card-description">{offer.description}</p>
                    <div className="card-credits">
                      <span className="credits-number">{offer.credits}</span>
                      <span className="credits-label">générations</span>
                    </div>
                    {offer.features && offer.features.length > 0 && (
                      <ul className="card-features">
                        {offer.features.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    className="card-button"
                    onClick={() => handleChooseOffer(offer.id)}
                    disabled={checkoutLoading === offer.id}
                  >
                    {checkoutLoading === offer.id ? 'Redirection...' : 'Choisir'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {subscription && subscription.status === 'active' && !subscription.cancel_at_period_end && (
        <div className="pricing-cancel">
          {!cancelConfirm ? (
            <button type="button" className="cancel-link" onClick={() => setCancelConfirm(true)}>
              Annuler mon abonnement
            </button>
          ) : (
            <div className="cancel-confirm">
              <p>Votre abonnement restera actif jusqu’à la fin de la période en cours.</p>
              <div className="cancel-actions">
                <button type="button" className="cancel-btn cancel-btn-confirm" onClick={handleCancelSubscription}>
                  Confirmer l’annulation
                </button>
                <button type="button" className="cancel-btn cancel-btn-back" onClick={() => setCancelConfirm(false)}>
                  Retour
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PricingPage;
